import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '@/shared/auth';
import { Icon } from '@/shared/components/ui/Icon';
import { formatRelativeTime } from '@/shared/i18n';
import { useI18n } from '@/shared/i18n/useT';
import { useOrg } from '@/shared/org';
import { Breadcrumb } from './components/Breadcrumb';
import { CreateMenu } from './components/CreateMenu';
import { MoveDialog } from './components/MoveDialog';
import { NodeCard } from './components/NodeCard';
import { NodeList } from './components/NodeList';
import { RenameDialog } from './components/RenameDialog';
import type { NodeAction } from './components/NodeMenu';
import {
  createFoldersShareAdapter,
  foldersApi as api,
  FOLDERS_RESOURCE_TYPE,
  TEMPLATES,
  editorPathFor,
  folderPath,
  useFoldersErrors,
} from '@/shared/folders';
import type { FoldersDocType, FoldersNode, FoldersPathStep } from '@/shared/folders';
import { ShareDialog } from '@/shared/sharing';

interface Props {
  /** The trash is the same browser over a different query, with two actions
   *  instead of six. A separate screen would duplicate the whole grid. */
  variant?: 'folder' | 'trash';
  /**
   * Rendered as a section of Home rather than as its own screen: Home already
   * shows Create and Trash at the top of the page, so the browser drops its own
   * header row and keeps only the breadcrumb. Navigation is unchanged — opening
   * a folder stays in Home's My files tab.
   */
  embedded?: boolean;
  /** Home's list/cards switch. The standalone browser always shows cards. */
  view?: 'list' | 'cards';
  /** The open folder; `null` is the root. Ignored by the trash. */
  folderId?: string | null;
}

export function FoldersBrowser({ variant = 'folder', embedded = false, view = 'cards', folderId = null }: Props) {
  const { t, formatLocale } = useI18n();
  const org = useOrg();
  const session = useSession();
  const navigate = useNavigate();

  const orgId = org.status === 'ready' ? org.orgId : null;
  const userId = session.session?.user.id ?? null;
  const readOnly = org.status === 'ready' && org.isViewingAs;
  const isTrash = variant === 'trash';
  const parentId = isTrash ? null : folderId;

  const [nodes, setNodes] = useState<FoldersNode[] | null>(null);
  const [path, setPath] = useState<readonly FoldersPathStep[]>([]);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadErrorKey, setLoadErrorKey] = useState<'folders.error.load' | 'folders.error.folderNotFound'>('folders.error.load');
  const [busy, setBusy] = useState(false);
  const [renaming, setRenaming] = useState<FoldersNode | null>(null);
  const [moving, setMoving] = useState<FoldersNode | null>(null);
  const [sharing, setSharing] = useState<FoldersNode | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [starredIds, setStarredIds] = useState<ReadonlySet<string>>(new Set());
  const { message: errorMessage, report, clear } = useFoldersErrors();

  const [reloadCount, setReloadCount] = useState(0);
  const reload = useCallback(() => setReloadCount((n) => n + 1), []);

  // Memoized because `ShareDialog` reloads whenever the adapter identity
  // changes; a new object every render would put it in a fetch loop.
  const shareAdapter = useMemo(
    () =>
      org.status === 'ready' && userId
        ? createFoldersShareAdapter({ userId, role: org.role })
        : null,
    [org, userId],
  );

  useEffect(() => {
    if (!orgId) return;
    const controller = new AbortController();
    setNodes(null);
    setLoadFailed(false);
    setLoadErrorKey('folders.error.load');

    void (async () => {
      try {
        const [children, steps, parent] = await Promise.all([
          isTrash
            ? api.listTrash(orgId, controller.signal)
            : api.listChildren(orgId, parentId, controller.signal),
          !isTrash && parentId
            ? api.getPath(parentId, controller.signal)
            : Promise.resolve([] as FoldersPathStep[]),
          !isTrash && parentId ? api.getNode(parentId, controller.signal) : Promise.resolve(null),
        ]);
        if (controller.signal.aborted) return;
        if (parentId && (!parent || parent.kind !== 'folder' || parent.orgId !== orgId)) {
          setNodes([]);
          setPath([]);
          setLoadErrorKey('folders.error.folderNotFound');
          setLoadFailed(true);
          return;
        }
        setNodes(children);
        setPath(steps);
      } catch {
        if (!controller.signal.aborted) {
          setNodes([]);
          setLoadFailed(true);
        }
      }
    })();

    return () => controller.abort();
  }, [orgId, parentId, isTrash, reloadCount]);

  // Separate from the listing above: a star is personal, it does not change
  // what is in the folder, and a failure to load one must not empty the grid.
  useEffect(() => {
    if (!orgId || isTrash) return;
    const controller = new AbortController();
    void api
      .listStarredIds(controller.signal)
      .then((ids) => { if (!controller.signal.aborted) setStarredIds(ids); })
      .catch(() => { /* the star simply stays unlit */ });
    return () => controller.abort();
  }, [orgId, isTrash, reloadCount]);

  const toggleStar = useCallback(
    (node: FoldersNode) => {
      if (!userId || readOnly) return;
      const wasStarred = starredIds.has(node.id);
      // Optimistic: the write is tiny, and a star that lags behind the click
      // reads as a broken control.
      setStarredIds((current) => {
        const next = new Set(current);
        if (wasStarred) next.delete(node.id);
        else next.add(node.id);
        return next;
      });
      void (wasStarred ? api.unstarNode(node.id, userId) : api.starNode(node.id, userId)).catch(
        (cause: unknown) => {
          setStarredIds((current) => {
            const next = new Set(current);
            if (wasStarred) next.add(node.id);
            else next.delete(node.id);
            return next;
          });
          report(cause, 'folders.error.load');
        },
      );
    },
    [readOnly, report, starredIds, userId],
  );

  /** Every write takes the same shape: block re-entry, clear the last message,
   *  report a failure in words, refresh on success. */
  const run = useCallback(
    async (action: () => Promise<void>, fallbackKey: Parameters<typeof report>[1]) => {
      if (busy || readOnly) return;
      setBusy(true);
      clear();
      setNotice(null);
      try {
        await action();
        reload();
      } catch (cause) {
        report(cause, fallbackKey);
      } finally {
        setBusy(false);
      }
    },
    [busy, clear, readOnly, reload, report],
  );

  const createFolder = () => {
    if (!orgId || readOnly) return;
    void run(async () => {
      await api.createFolder(orgId, parentId, t('folders.newName.folder'), userId);
    }, 'folders.error.create');
  };

  const emptyTrash = () => {
    if (!orgId || !isTrash || !nodes || nodes.length === 0 || readOnly) return;
    if (!window.confirm(t('folders.emptyTrashDialog.title'))) return;
    void run(() => api.emptyTrash(orgId), 'folders.error.remove');
  };

  const createDocument = (docType: FoldersDocType) => {
    if (!orgId || readOnly) return;
    const template = TEMPLATES[docType];
    void run(async () => {
      const node = await api.createDocument(
        orgId,
        parentId,
        docType,
        template.defaultName(t),
        template.defaultTabName(t),
        template.columns(t),
        template.initialRows,
        userId,
        template.additionalTabs?.(t),
      );
      // Straight into the editor: creating a file and then having to find it
      // is the wrong end of the interaction.
      navigate(editorPathFor(node.id, node.docType));
    }, 'folders.error.create');
  };

  const copyLink = async (node: FoldersNode) => {
    const url = new URL(
      node.kind === 'folder' ? folderPath(node.id) : editorPathFor(node.id, node.docType),
      window.location.origin,
    ).toString();
    try {
      await navigator.clipboard.writeText(url);
      setNotice(t('folders.linkCopied'));
    } catch {
      // Clipboard access can be refused; showing the URL beats claiming success.
      setNotice(url);
    }
  };

  const actionsFor = useCallback(
    (node: FoldersNode): NodeAction[] => {
      if (readOnly) {
        return [{ id: 'link', labelKey: 'folders.copyLink', icon: 'link', run: () => void copyLink(node) }];
      }
      if (isTrash) {
        return [
          {
            id: 'restore',
            labelKey: 'folders.restore',
            icon: 'restore',
            run: () => void run(() => api.restoreNode(node.id), 'folders.error.restore'),
          },
          {
            id: 'delete',
            labelKey: 'folders.deleteForever',
            icon: 'trash',
            danger: true,
            run: () => void run(() => api.deleteNode(node.id), 'folders.error.remove'),
          },
        ];
      }

      const actions: NodeAction[] = [
        {
          id: 'star',
          labelKey: starredIds.has(node.id) ? 'folders.unstar' : 'folders.star',
          icon: 'star',
          run: () => toggleStar(node),
        },
        { id: 'rename', labelKey: 'folders.rename', icon: 'pencil', run: () => setRenaming(node) },
        { id: 'move', labelKey: 'folders.move', icon: 'folderMove', run: () => setMoving(node) },
        // Sharing a folder reaches everything inside it, which is why it is
        // offered on both kinds.
        { id: 'share', labelKey: 'folders.share', icon: 'user', run: () => setSharing(node) },
        { id: 'link', labelKey: 'folders.copyLink', icon: 'link', run: () => void copyLink(node) },
      ];

      // Folders are not duplicable: a deep copy of a tree is a different
      // feature, and one menu item that behaves two ways is worse than two.
      if (node.kind === 'document') {
        actions.push({
          id: 'duplicate',
          labelKey: 'folders.duplicate',
          icon: 'sheets',
          run: () =>
            void run(async () => {
              await api.duplicateDocument(node, `${node.name} (2)`, userId);
            }, 'folders.error.create'),
        });
      }

      actions.push({
        id: 'trash',
        labelKey: 'folders.sendToTrash',
        icon: 'trash',
        danger: true,
        run: () => void run(() => api.trashNode(node.id), 'folders.error.trash'),
      });

      return actions;
    },
    [copyLink, isTrash, readOnly, run, starredIds, toggleStar, userId, t],
  );

  const { folders, files } = useMemo(() => {
    const all = nodes ?? [];
    return {
      folders: all.filter((node) => node.kind === 'folder'),
      files: all.filter((node) => node.kind === 'document'),
    };
  }, [nodes]);

  if (org.status === 'loading' || nodes === null) {
    return <p className="text-sm text-muted">{t('common.loading')}</p>;
  }

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        {isTrash ? (
          !embedded && <h1 className="min-w-0 truncate text-lg font-semibold text-fg">{t('folders.trash')}</h1>
        ) : (
          <Breadcrumb path={path} />
        )}

        <div className={`ml-auto flex items-center gap-2 ${embedded && !isTrash ? 'hidden' : ''}`}>
          {isTrash && nodes.length > 0 && (
            <button
              type="button"
              aria-label={t('folders.emptyTrash')}
              onClick={emptyTrash}
              disabled={busy || readOnly || !orgId}
              className="tap-target flex items-center gap-2 rounded-full px-3 text-sm text-danger hover:bg-danger-soft disabled:opacity-40"
            >
              <Icon name="trash" className="h-4 w-4" />
              <span>{t('folders.emptyTrash')}</span>
            </button>
          )}
          {!isTrash && !embedded && (
            <button
              type="button"
              aria-label={t('folders.trash')}
              onClick={() => navigate('/folders/trash')}
              className="tap-target flex items-center gap-2 rounded-full px-3 text-sm text-muted hover:bg-surface-3 hover:text-fg"
            >
              <Icon name="trash" className="h-4 w-4" />
              <span className="hidden sm:inline">{t('folders.trash')}</span>
            </button>
          )}
          {!isTrash && !embedded && (
            <CreateMenu
              onCreateFolder={createFolder}
              onCreateDocument={createDocument}
              disabled={busy || !orgId || readOnly}
            />
          )}
        </div>
      </div>

      {loadFailed && (
        <p role="alert" className="rounded-lg border border-danger p-3 text-sm text-danger">
          {t(loadErrorKey)}
        </p>
      )}
      {errorMessage && (
        <p role="alert" className="rounded-token border border-danger p-3 text-sm text-danger">
          {errorMessage}
        </p>
      )}
      {notice && <p className="text-sm text-muted">{notice}</p>}

      {nodes.length === 0 && !loadFailed ? (
        <div className="rounded-token border border-dashed border-border p-8 text-center">
          <p className="text-base font-medium text-fg">
            {t(isTrash ? 'folders.emptyTrashTitle' : 'folders.emptyTitle')}
          </p>
          <p className="mt-1 text-sm text-muted">
            {t(isTrash ? 'folders.emptyTrashBody' : 'folders.emptyBody')}
          </p>
        </div>
      ) : (
        <>
          {view === 'list' ? (
            <NodeList
              dateLabel={t('home.columns.edited')}
              rows={[...folders, ...files].map((node) => ({
                node,
                actions: actionsFor(node),
                date: formatRelativeTime(node.updatedAt, formatLocale),
                starred: starredIds.has(node.id),
                ...(isTrash || readOnly ? {} : { onToggleStar: () => toggleStar(node) }),
              }))}
            />
          ) : (
            <>
            {folders.length > 0 && (
              <section>
                <h2 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted">
                  {t('folders.folders')}
                </h2>
                <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                  {folders.map((node) => (
                    <li key={node.id}>
                      <NodeCard
                        node={node}
                        actions={actionsFor(node)}
                        starred={starredIds.has(node.id)}
                        {...(isTrash || readOnly ? {} : { onToggleStar: () => toggleStar(node) })}
                      />
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {files.length > 0 && (
              <section>
                <h2 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted">
                  {t('folders.files')}
                </h2>
                <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                  {files.map((node) => (
                    <li key={node.id}>
                      <NodeCard
                        node={node}
                        actions={actionsFor(node)}
                        starred={starredIds.has(node.id)}
                        {...(isTrash || readOnly ? {} : { onToggleStar: () => toggleStar(node) })}
                      />
                    </li>
                  ))}
                </ul>
              </section>
            )}
            </>
          )}
        </>
      )}

      {renaming && (
        <RenameDialog
          initialName={renaming.name}
          onClose={() => setRenaming(null)}
          onSubmit={(name) => {
            const target = renaming;
            setRenaming(null);
            void run(() => api.renameNode(target.id, name), 'folders.error.rename');
          }}
        />
      )}

      {moving && orgId && (
        <MoveDialog
          node={moving}
          orgId={orgId}
          onClose={() => setMoving(null)}
          onSubmit={(destination) => {
            const target = moving;
            setMoving(null);
            void run(() => api.moveNode(target.id, destination), 'folders.error.move');
          }}
        />
      )}

      {sharing && shareAdapter && (
        <ShareDialog
          subject={{
            kind: 'resource',
            orgId: sharing.orgId,
            resourceType: FOLDERS_RESOURCE_TYPE,
            resourceId: sharing.id,
            name: sharing.name,
          }}
          adapter={shareAdapter}
          onClose={() => setSharing(null)}
        />
      )}
    </div>
  );
}




